import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input, Label, Textarea } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  ClipboardCheck, Sparkles, Download, Plus, FileText, CheckCircle2, AlertTriangle, XCircle,
  ShieldCheck, FolderOpen, Building2, Lightbulb, ChevronDown, ChevronRight, ExternalLink, Circle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  comply: { label: "Comply", tone: "success", icon: CheckCircle2, color: "text-[#00E65B]" },
  partial: { label: "Partial", tone: "warning", icon: AlertTriangle, color: "text-[#FFB800]" },
  not_complied: { label: "Not Complied", tone: "danger", icon: XCircle, color: "text-red-500" },
};
const EV_STATUS = { available: "success", in_progress: "warning", missing: "danger" };
const band = (p) => (p >= 80 ? "text-[#00E65B]" : p >= 50 ? "text-[#FFB800]" : "text-red-500");

function Stat({ label, value, unit, sub, icon: Icon, testid, tone }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span></div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ChecklistItem({ cid, item, onSaved }) {
  const [status, setStatus] = useState(item.override_status || "auto");
  const [remarks, setRemarks] = useState(item.remarks || "");
  const [open, setOpen] = useState(false);
  const eff = STATUS_META[item.effective_status];
  const save = async (overrideStatus, rmk) => {
    try {
      await api.patch(`/customer-compliance/checklists/${cid}/items/${item.id}`, {
        override_status: overrideStatus === "auto" ? null : overrideStatus, remarks: rmk });
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openModule = () => {
    if (item.module) window.open(`${window.location.origin}${item.module.to}`, "_blank", "noopener");
  };
  const dotTone = { ok: "text-[#00E65B]", warn: "text-[#FFB800]", bad: "text-red-500" };
  return (
    <div className="border border-border rounded-sm p-3 space-y-2" data-testid={`cc-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <Badge variant={eff.tone} className="text-[9px] shrink-0 mt-0.5"><eff.icon className="h-3 w-3 mr-1" />{eff.label}</Badge>
        <div className="flex-1">
          <div className="text-sm">{item.sn}. {item.description}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Auto: <span className={STATUS_META[item.auto_status].color}>{STATUS_META[item.auto_status].label}</span> — {item.auto_detail}</div>
          {item.effective_status === "not_complied" && item.gap_hint && (
            <div className="text-[11px] text-[#FFB800] mt-1 flex items-start gap-1"><Lightbulb className="h-3 w-3 mt-0.5 shrink-0" />{item.gap_hint}</div>
          )}
          <button onClick={() => setOpen((o) => !o)} className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-accent hover:underline" data-testid={`cc-item-drill-${item.id}`}>
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} {open ? "Hide" : "View"} supporting data
          </button>
        </div>
      </div>

      {open && (
        <div className="ml-1 rounded-sm bg-surface-hover/50 border border-border p-3 space-y-2" data-testid={`cc-item-breakdown-${item.id}`}>
          <div className="space-y-1.5">
            {(item.breakdown || []).map((b, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Circle className={`h-2 w-2 fill-current ${dotTone[b.status] || "text-muted-foreground"}`} />{b.label}</span>
                <span className={`font-mono ${dotTone[b.status] || ""}`}>{b.value}</span>
              </div>
            ))}
            {!(item.breakdown || []).length && <div className="text-xs text-muted-foreground">No supporting data points.</div>}
          </div>
          {item.module && (
            <button onClick={openModule} className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline pt-1" data-testid={`cc-item-link-${item.id}`}>
              <ExternalLink className="h-3.5 w-3.5" /> Open {item.module.label} in a new tab
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pl-1">
        <Select value={status} onValueChange={(v) => { setStatus(v); save(v, remarks); }}>
          <SelectTrigger className="h-7 w-[150px] text-xs" data-testid={`cc-item-status-${item.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto (platform)</SelectItem>
            {Object.keys(STATUS_META).map((k) => <SelectItem key={k} value={k}>Override: {STATUS_META[k].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} onBlur={() => save(status, remarks)}
          placeholder="Remarks / evidence note…" className="h-7 text-xs flex-1 min-w-[180px]" data-testid={`cc-item-remarks-${item.id}`} />
      </div>
    </div>
  );
}

function CreatePack({ templates, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ customer_name: "", project_ref: "", project_name: "", description: "", template_codes: ["MSC", "FSC"] });
  const toggle = (c) => setF((p) => ({ ...p, template_codes: p.template_codes.includes(c) ? p.template_codes.filter((x) => x !== c) : [...p.template_codes, c] }));
  const create = async () => {
    try { const r = await api.post("/customer-compliance/packs", f); setOpen(false); onCreated(r.data.id); toast.success("Compliance pack created"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="primary" size="sm" data-testid="cc-create-btn"><Plus className="h-4 w-4" /> New pack</Button></DialogTrigger>
      <DialogContent data-testid="cc-create-dialog">
        <DialogHeader><DialogTitle className="font-display">New customer compliance pack</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Customer name</Label><Input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="cc-input-customer" placeholder="e.g. Mandai Wildlife Group" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Project ref</Label><Input value={f.project_ref} onChange={(e) => setF({ ...f, project_ref: e.target.value })} /></div>
            <div><Label>Project name</Label><Input value={f.project_name} onChange={(e) => setF({ ...f, project_name: e.target.value })} /></div>
          </div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} /></div>
          <div>
            <Label>Checklists to include</Label>
            <div className="flex flex-col gap-2 mt-1">
              {templates.map((t) => (
                <label key={t.code} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={f.template_codes.includes(t.code)} onChange={() => toggle(t.code)} className="accent-[hsl(var(--accent))]" data-testid={`cc-tpl-${t.code}`} />{t.name}
                </label>
              ))}
            </div>
          </div>
          <Button variant="primary" onClick={create} disabled={!f.customer_name || !f.template_codes.length} data-testid="cc-create-submit">Create pack</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddEvidence({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ key: "", type: "policy", name: "", description: "", status: "available" });
  const save = async () => {
    const key = f.key || f.name.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40);
    try { await api.post("/customer-compliance/evidence", { ...f, key }); setOpen(false); setF({ key: "", type: "policy", name: "", description: "", status: "available" }); onSaved(); toast.success("Evidence saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="cc-add-evidence-btn"><Plus className="h-4 w-4" /> Add evidence</Button></DialogTrigger>
      <DialogContent data-testid="cc-evidence-dialog">
        <DialogHeader><DialogTitle className="font-display">Add compliance evidence</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cc-evidence-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["policy", "training", "certificate", "record", "procedure"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["available", "in_progress", "missing"].map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} /></div>
          <Button variant="primary" onClick={save} disabled={!f.name} data-testid="cc-evidence-submit">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerCompliance() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "checklists");
  const [meta, setMeta] = useState({ templates: [] });
  const [packs, setPacks] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [pack, setPack] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [ai, setAi] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPacks = useCallback(() => {
    api.get("/customer-compliance/packs").then((r) => {
      setPacks(r.data);
      setActiveId((cur) => cur || (r.data[0] && r.data[0].id) || null);
    });
  }, []);
  useEffect(() => {
    api.get("/customer-compliance/meta").then((r) => setMeta(r.data));
    api.get("/customer-compliance/evidence").then((r) => setEvidence(r.data));
    api.get("/customer-compliance/suppliers").then((r) => setSuppliers(r.data));
    loadPacks();
  }, [loadPacks]);

  const loadPack = useCallback(() => {
    if (!activeId) { setPack(null); return; }
    api.get(`/customer-compliance/packs/${activeId}`).then((r) => setPack(r.data)).catch(() => setPack(null));
  }, [activeId]);
  useEffect(() => { setAi(""); loadPack(); }, [loadPack]);
  const reloadEvidence = () => { api.get("/customer-compliance/evidence").then((r) => setEvidence(r.data)); loadPack(); loadPacks(); };

  const genAi = async () => { setBusy(true); try { const r = await api.post(`/customer-compliance/packs/${activeId}/ai/answers`); setAi(r.data.cover_statement); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const setEvStatus = async (e, status) => { try { await api.post("/customer-compliance/evidence", { key: e.key, type: e.type, name: e.name, description: e.description || "", status }); reloadEvidence(); } catch { toast.error("Failed"); } };
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/customer-compliance/packs/${activeId}/export.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "compliance_checklist.pdf"; a.click();
      window.URL.revokeObjectURL(url); toast.success("Checklist exported");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (packs === null) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading customer compliance…</div>;

  const evAvail = evidence.filter((e) => e.status === "available").length;
  const mscReady = suppliers.filter((s) => s.msc_ready).length;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="customer-compliance-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Reporting & Compliance · Customer Compliance</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><ClipboardCheck className="h-8 w-8 text-accent" /> Customer Compliance</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Respond to customer & tender sustainability checklists (MSC, FSC and more). Items are auto-assessed from your platform data — override, add evidence, track subcontractors and export a signed checklist.</p>
        </div>
        <div className="flex items-center gap-2">
          {packs.length > 0 && (
            <Select value={activeId || ""} onValueChange={setActiveId}>
              <SelectTrigger className="w-[260px]" data-testid="cc-pack-select"><SelectValue placeholder="Select pack" /></SelectTrigger>
              <SelectContent>{packs.map((p) => <SelectItem key={p.id} value={p.id}>{p.customer_name} — {p.compliance_pct}%</SelectItem>)}</SelectContent>
            </Select>
          )}
          <CreatePack templates={meta.templates} onCreated={(id) => { setActiveId(id); loadPacks(); }} />
        </div>
      </div>

      {!pack ? (
        <div className="card-flat p-12 text-center">
          <ClipboardCheck className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h2 className="font-display text-2xl font-bold">No compliance pack selected.</h2>
          <p className="text-muted-foreground mt-2">Create a pack for a customer/tender to auto-build MSC & FSC checklists from your platform data.</p>
        </div>
      ) : (
        <>
          <div className="card-flat p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium flex items-center gap-2"><Building2 className="h-4 w-4 text-accent" />{pack.customer_name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{pack.project_name} {pack.project_ref ? `· ${pack.project_ref}` : ""} {pack.due_date ? `· due ${pack.due_date}` : ""}</div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="cc-export-btn"><Download className="h-4 w-4" /> Export PDF</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat label="Overall compliance" value={pack.compliance_pct} unit="%" icon={ShieldCheck} testid="cc-stat-overall" tone={band(pack.compliance_pct)} />
            <Stat label="Checklists" value={pack.checklists.length} unit="" icon={ClipboardCheck} testid="cc-stat-checklists" />
            <Stat label="Evidence on file" value={evAvail} unit={`/${evidence.length}`} icon={FolderOpen} testid="cc-stat-evidence" />
            <Stat label="Suppliers MSC-ready" value={mscReady} unit={`/${suppliers.length}`} icon={Building2} testid="cc-stat-suppliers" />
            {pack.checklists.map((cl) => (
              <Stat key={cl.id} label={cl.code} value={cl.compliance_pct} unit="%" icon={FileText} tone={band(cl.compliance_pct)} />
            ))}
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="checklists" data-testid="cc-tab-checklists">Checklists</TabsTrigger>
              <TabsTrigger value="suppliers" data-testid="cc-tab-suppliers">Subcontractor Compliance</TabsTrigger>
              <TabsTrigger value="evidence" data-testid="cc-tab-evidence">Evidence Library</TabsTrigger>
              <TabsTrigger value="statement" data-testid="cc-tab-statement">AI Cover Statement</TabsTrigger>
            </TabsList>

            <TabsContent value="checklists" className="mt-4 space-y-5">
              {pack.checklists.map((cl) => (
                <div key={cl.id} className="card-flat p-4" data-testid={`cc-checklist-${cl.code}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div><div className="font-display text-lg font-bold">{cl.name}</div><div className="text-[11px] text-muted-foreground">{cl.full_name}</div></div>
                    <span className={`font-mono text-xl font-bold ${band(cl.compliance_pct)}`}>{cl.compliance_pct}%</span>
                  </div>
                  {cl.sections.map((sec) => (
                    <div key={sec.key} className="mb-3">
                      <div className="label-eyebrow mb-2">{sec.title}</div>
                      <div className="space-y-2">
                        {sec.items.map((it) => <ChecklistItem key={it.id} cid={cl.id} item={it} onSaved={() => { loadPack(); loadPacks(); }} />)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </TabsContent>

            <TabsContent value="suppliers" className="mt-4">
              <div className="card-flat overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Supplier</TableHead><TableHead>Category</TableHead><TableHead>Certifications</TableHead>
                    <TableHead>Code of Conduct</TableHead><TableHead>Screened</TableHead><TableHead>MSC ready</TableHead><TableHead>FSC ready</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {suppliers.map((s) => (
                      <TableRow key={s.id} data-testid={`cc-supplier-row-${s.id}`}>
                        <TableCell className="text-sm font-medium">{s.name}<div className="text-[10px] text-muted-foreground">{s.country}</div></TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{s.category}</TableCell>
                        <TableCell><div className="flex flex-wrap gap-1">{s.certifications.length ? s.certifications.map((c) => <Badge key={c} variant="outline" className="text-[9px]">{c}</Badge>) : <span className="text-xs text-muted-foreground">—</span>}</div></TableCell>
                        <TableCell>{s.code_of_conduct ? <CheckCircle2 className="h-4 w-4 text-[#00E65B]" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}</TableCell>
                        <TableCell>{s.screened ? <CheckCircle2 className="h-4 w-4 text-[#00E65B]" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}</TableCell>
                        <TableCell><Badge variant={s.msc_ready ? "success" : "danger"} className="text-[9px]">{s.msc_ready ? "Ready" : "Gap"}</Badge></TableCell>
                        <TableCell><Badge variant={s.fsc_ready ? "success" : "outline"} className="text-[9px]">{s.fsc_ready ? "Certified" : "—"}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">All proposed subcontractors/suppliers must complete the MSC & FSC checklists before award. Data sourced from Supplier Insights.</p>
            </TabsContent>

            <TabsContent value="evidence" className="mt-4 space-y-3">
              <div className="flex justify-end"><AddEvidence onSaved={reloadEvidence} /></div>
              <div className="card-flat overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Evidence</TableHead><TableHead>Type</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {evidence.map((e) => (
                      <TableRow key={e.key} data-testid={`cc-evidence-row-${e.key}`}>
                        <TableCell className="text-sm font-medium">{e.name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[9px]">{e.type}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[280px]">{e.description}</TableCell>
                        <TableCell>
                          <Select value={e.status} onValueChange={(v) => setEvStatus(e, v)}>
                            <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`cc-evidence-status-${e.key}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{["available", "in_progress", "missing"].map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!evidence.length && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground text-sm">No evidence yet. Add policies, training records and certificates here.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="statement" className="mt-4">
              <div className="card-flat p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI compliance cover statement</div>
                  <Button size="sm" variant="primary" onClick={genAi} disabled={busy} data-testid="cc-ai-btn">{busy ? "Generating…" : "Generate statement"}</Button>
                </div>
                {ai ? <p className="text-sm leading-relaxed" data-testid="cc-ai-text">{ai}</p> : <p className="text-sm text-muted-foreground">Generate a customer-ready cover statement summarising your compliance, evidence and how any gaps are being closed.</p>}
                <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#00E65B]" /> Use this alongside the exported PDF checklist for your tender submission.
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
